import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './ManualEventForm.css';
import TimeInputModal from './TimeInputModal';
import { useFamilyParticipants } from '../hooks/useFamilyParticipants';
import { removeFamilyMember, getUserFamilyInfo, addCustomParticipantToFamily, removeCustomParticipantFromFamily } from '../services/familyService';
import { useAuth } from '../contexts/AuthContext';
import { useClassRole } from '../hooks/useClassRole';
import { trackCalendar, trackScreenView } from '../utils/analytics';

const ManualEventForm = ({ onAddEvent, API_KEY, eventToEdit, initialDate, initialHour, onClose }) => {
  // Get the current date every time the component renders
  const getCurrentDate = () => new Date();
  
  // Check if we're on mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Get family participants
  const { participants: familyParticipants, refreshParticipants } = useFamilyParticipants();
  const { currentUser } = useAuth();
  const { isClassAdmin, classInfo } = useClassRole();
  const [userFamilyInfo, setUserFamilyInfo] = useState(null);
  const navigate = useNavigate();
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Fetch user's family info and track screen view
  useEffect(() => {
    const fetchFamilyInfo = async () => {
      if (currentUser) {
        const familyInfo = await getUserFamilyInfo(currentUser.uid);
        setUserFamilyInfo(familyInfo);
      }
    };
    fetchFamilyInfo();
    
    // Track screen view
    trackScreenView(eventToEdit ? 'EditEventForm' : 'CreateEventForm');
  }, [currentUser, eventToEdit]);
  
  const [showDetailedForm, setShowDetailedForm] = useState(true);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [expandRecurring, setExpandRecurring] = useState(false);
  const [showStartTimeModal, setShowStartTimeModal] = useState(false);
  const [showEndTimeModal, setShowEndTimeModal] = useState(false);
  const [showParticipantEditor, setShowParticipantEditor] = useState(false);
  const [customParticipants, setCustomParticipants] = useState([]);
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [savedParticipantSelection, setSavedParticipantSelection] = useState([]);
  // Helper function to convert 24-hour to 12-hour format
  const convert24To12Hour = (hour24) => {
    if (hour24 === 0) return { hour: '12', amPm: 'AM' };
    if (hour24 < 12) return { hour: String(hour24).padStart(2, '0'), amPm: 'AM' };
    if (hour24 === 12) return { hour: '12', amPm: 'PM' };
    return { hour: String(hour24 - 12).padStart(2, '0'), amPm: 'PM' };
  };

  // Calculate initial start time
  const getInitialStartTime = () => {
    if (initialHour !== null && initialHour !== undefined) {
      return convert24To12Hour(initialHour);
    }
    return { hour: '09', amPm: 'AM' };
  };

  // Calculate initial end time (1 hour after start)
  const getInitialEndTime = () => {
    if (initialHour !== null && initialHour !== undefined) {
      const endHour24 = (initialHour + 1) % 24;
      return convert24To12Hour(endHour24);
    }
    return { hour: '10', amPm: 'AM' };
  };

  const initialStartTime = getInitialStartTime();
  const initialEndTime = getInitialEndTime();

  const [formData, setFormData] = useState({
    eventName: '',
    description: '',
    date: initialDate || getCurrentDate().toISOString().split('T')[0],
    hasTime: true,
    isAllDay: false,
    startHour: initialStartTime.hour,
    startMinute: '00',
    startAmPm: initialStartTime.amPm,
    endHour: initialEndTime.hour,
    endMinute: '00',
    endAmPm: initialEndTime.amPm,
    timeInputValue: initialStartTime.hour + '00', // For start time input
    endTimeInputValue: initialEndTime.hour + '00', // For end time input
    eventDuration: 60, // Default duration in minutes (1 hour)
    participants: [],
    location: '',
    category: 'life',
    isRecurring: false,
    recurrencePattern: 'custom', // Always use custom as default
    recurrenceEndDate: '',
    recurrenceCount: 0,
    recurrenceInterval: 1,
    selectedDays: {
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false
    }
  });

  // Common locations
  const [commonLocations, setCommonLocations] = useState([
    { id: 'home', name: 'Home' },
    { id: 'school', name: 'School' }
  ]);
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [newLocationName, setNewLocationName] = useState('');
  
  // Common participants - use family members when available
  const commonParticipants = familyParticipants;

  // Event categories with auto-assign rules
  const baseCategories = [
    { id: 'school', name: 'School', keywords: ['school', 'class', 'study', 'homework', 'lesson', 'teacher', 'student'] },
    { id: 'work', name: 'Work', keywords: ['work', 'meeting', 'client', 'project', 'deadline', 'presentation', 'report', 'office', 'business'] },
    { id: 'sport', name: 'Sport', keywords: ['sport', 'gym', 'fitness', 'workout', 'training', 'game', 'match', 'swim', 'run', 'tennis'] },
    { id: 'life', name: 'Life', keywords: ['appointment', 'doctor', 'dentist', 'haircut', 'shopping', 'dinner', 'lunch', 'party', 'celebration'] }
  ];

  // Add class category if user is a class admin
  const categories = isClassAdmin 
    ? [...baseCategories, { id: 'class', name: 'Class', keywords: ['class event', 'class activity', 'class meeting'] }]
    : baseCategories;
  
  // Auto-assign category based on form inputs
  useEffect(() => {
    if (formData.eventName || formData.description || formData.location) {
      const textToAnalyze = `${formData.eventName} ${formData.description} ${formData.location}`.toLowerCase();
      let matchedCategory = 'life'; // Default
      
      if (textToAnalyze.includes('work') || 
          textToAnalyze.includes('meeting') || 
          textToAnalyze.includes('client') || 
          textToAnalyze.includes('project') || 
          textToAnalyze.includes('deadline') || 
          textToAnalyze.includes('presentation') || 
          textToAnalyze.includes('report') || 
          textToAnalyze.includes('office') || 
          textToAnalyze.includes('business')) {
        matchedCategory = 'work';
      }
      else if (textToAnalyze.includes('school') || 
              textToAnalyze.includes('class') || 
              textToAnalyze.includes('study') || 
              textToAnalyze.includes('homework') || 
              textToAnalyze.includes('lesson') || 
              textToAnalyze.includes('teacher') || 
              textToAnalyze.includes('student') || 
              textToAnalyze.includes('academic')) {
        matchedCategory = 'school';
      }
      else if (textToAnalyze.includes('sport') || 
              textToAnalyze.includes('gym') || 
              textToAnalyze.includes('fitness') || 
              textToAnalyze.includes('workout') || 
              textToAnalyze.includes('training') || 
              textToAnalyze.includes('game') || 
              textToAnalyze.includes('match') || 
              textToAnalyze.includes('swim') || 
              textToAnalyze.includes('run') ||
              textToAnalyze.includes('tennis')) {
        matchedCategory = 'sport';
      }
      
      if (matchedCategory !== formData.category) {
        setFormData(prev => ({ ...prev, category: matchedCategory }));
      }
    }
  }, [formData.eventName, formData.description, formData.location]);

  // Get all available participants (now comes from the hook which includes custom participants)
  const getAllParticipants = () => {
    return familyParticipants; // Hook now returns both family and custom participants
  };

  // Add a new custom participant to Firestore
  const addCustomParticipant = async (name, role = 'child') => {
    if (!userFamilyInfo?.familyId) {
      console.error('No family ID found');
      return;
    }

    try {
      const result = await addCustomParticipantToFamily(userFamilyInfo.familyId, name, role);
      if (result.success) {
        console.log('Custom participant added successfully');
        // Refresh the participants list immediately
        refreshParticipants();
        return result.participant.id;
      } else {
        console.error('Failed to add custom participant:', result.error);
        alert(result.error || 'Failed to add participant');
      }
    } catch (error) {
      console.error('Error adding custom participant:', error);
    }
  };

  // Remove a custom participant from Firestore
  const removeCustomParticipant = async (participantId) => {
    if (!userFamilyInfo?.familyId) {
      console.error('No family ID found');
      return;
    }

    try {
      const result = await removeCustomParticipantFromFamily(userFamilyInfo.familyId, participantId);
      if (result.success) {
        // Remove from form data if selected
        setFormData(prev => ({
          ...prev,
          participants: prev.participants.filter(id => id !== participantId)
        }));
        console.log('Custom participant removed successfully');
        // Refresh the participants list immediately
        refreshParticipants();
      } else {
        console.error('Failed to remove custom participant:', result.error);
      }
    } catch (error) {
      console.error('Error removing custom participant:', error);
    }
  };

  // Remove family member permanently
  const removeFamilyMemberPermanently = async (participantId) => {
    if (!userFamilyInfo?.familyId) {
      console.error('No family ID found');
      return;
    }

    try {
      const result = await removeFamilyMember(userFamilyInfo.familyId, participantId);
      if (result.success) {
        // Remove from form data if selected
        setFormData(prev => ({
          ...prev,
          participants: prev.participants.filter(id => id !== participantId)
        }));
        // Refresh the participants list immediately
        refreshParticipants();
        console.log('Family member removed successfully');
      } else {
        console.error('Failed to remove family member:', result.error);
      }
    } catch (error) {
      console.error('Error removing family member:', error);
    }
  };

  // Handle remove button click
  const handleRemoveClick = async (participant) => {
    if (participant.custom) {
      // Remove custom participant immediately
      await removeCustomParticipant(participant.id);
    } else {
      // Show confirmation for family members
      setMemberToRemove(participant);
      setShowRemoveConfirmation(true);
    }
  };

  // Confirm family member removal
  const confirmRemoveMember = async () => {
    if (memberToRemove) {
      await removeFamilyMemberPermanently(memberToRemove.id);
      setShowRemoveConfirmation(false);
      setMemberToRemove(null);
    }
  };

  // Redirect to family share page instead of opening modal
  const openParticipantEditor = () => {
    navigate('/family-share');
  };

  // Close participant editor and restore selection
  const closeParticipantEditor = () => {
    console.log('🔄 BEFORE Closing participant editor. Current selection:', formData.participants || []);
    console.log('🔄 BEFORE Closing participant editor. Saved selection:', savedParticipantSelection || []);
    
    setShowParticipantEditor(false);
    
    // Restore saved selection immediately
    if (savedParticipantSelection && savedParticipantSelection.length > 0) {
      setFormData(prev => ({
        ...prev,
        participants: [...savedParticipantSelection]
      }));
      console.log('🔄 RESTORED selection to:', savedParticipantSelection);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Convert ISO date format to display format (DD/MM/YYYY)
  const formatDateForDisplay = (isoDate) => {
    if (!isoDate) return '';
    const parts = isoDate.split('-');
    if (parts.length !== 3) return isoDate;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };
  
  // Convert display format (DD/MM/YYYY) to ISO format (YYYY-MM-DD)
  const formatDateForStorage = (displayDate) => {
    if (!displayDate) return '';
    const parts = displayDate.split('/');
    if (parts.length !== 3) return displayDate;
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  };

  // Initialize with event data if editing
  useEffect(() => {
    if (eventToEdit && commonParticipants && commonParticipants.length > 0) {
      // Format date to YYYY-MM-DD for form data
      const dateStr = `${eventToEdit.year}-${String(eventToEdit.month).padStart(2, '0')}-${String(eventToEdit.day).padStart(2, '0')}`;
      
      // Format date to DD/MM/YYYY for display
      const displayDateStr = `${String(eventToEdit.day).padStart(2, '0')}/${String(eventToEdit.month).padStart(2, '0')}/${eventToEdit.year}`;
      
      let startHour = '09';
      let startMinute = '00';
      let startAmPm = 'AM';
      
      // Convert 24-hour to 12-hour format if time exists
      if (eventToEdit.hasTime && eventToEdit.hour !== null && eventToEdit.minute !== null) {
        if (eventToEdit.hour === 0) {
          startHour = '12';
          startAmPm = 'AM';
        } else if (eventToEdit.hour < 12) {
          startHour = String(eventToEdit.hour).padStart(2, '0');
          startAmPm = 'AM';
        } else if (eventToEdit.hour === 12) {
          startHour = '12';
          startAmPm = 'PM';
        } else {
          startHour = String(eventToEdit.hour - 12).padStart(2, '0');
          startAmPm = 'PM';
        }
        
        startMinute = String(eventToEdit.minute).padStart(2, '0');
      }
      
      // Set up end time - either from existing event or calculate based on duration
      let endHour = '10';
      let endMinute = '00';
      let endAmPm = startAmPm; // Default to same AM/PM as start time
      
      if (eventToEdit.hasTime) {
        // If event has end time directly specified
        if (eventToEdit.endHour !== undefined && eventToEdit.endHour !== null) {
          if (eventToEdit.endHour === 0) {
            endHour = '12';
            endAmPm = 'AM';
          } else if (eventToEdit.endHour < 12) {
            endHour = String(eventToEdit.endHour).padStart(2, '0');
            endAmPm = 'AM';
          } else if (eventToEdit.endHour === 12) {
            endHour = '12';
            endAmPm = 'PM';
          } else {
            endHour = String(eventToEdit.endHour - 12).padStart(2, '0');
            endAmPm = 'PM';
          }
          
          endMinute = String(eventToEdit.endMinute || 0).padStart(2, '0');
        } 
        // If event has duration but no end time, calculate end time
        else if (eventToEdit.eventDuration && eventToEdit.hour !== null) {
          const startMinutes = (eventToEdit.hour * 60) + (eventToEdit.minute || 0);
          const endMinutes = startMinutes + (eventToEdit.eventDuration || 60);
          
          let endHour24 = Math.floor(endMinutes / 60) % 24;
          const calcEndMinute = endMinutes % 60;
          
          // Convert to 12-hour format
          if (endHour24 === 0) {
            endHour = '12';
            endAmPm = 'AM';
          } else if (endHour24 < 12) {
            endHour = String(endHour24).padStart(2, '0');
            endAmPm = 'AM';
          } else if (endHour24 === 12) {
            endHour = '12';
            endAmPm = 'PM';
          } else {
            endHour = String(endHour24 - 12).padStart(2, '0');
            endAmPm = 'PM';
          }
          
          endMinute = String(calcEndMinute).padStart(2, '0');
        } 
        // Default end time is 1 hour after start time
        else {
          const startHourNum = parseInt(startHour, 10);
          // If start time is 12, end hour depends on AM/PM
          if (startHourNum === 12) {
            if (startAmPm === 'AM') {
              endHour = '01';
              endAmPm = 'PM';
            } else {
              endHour = '01';
              endAmPm = 'AM'; // Next day
            }
          } else if (startHourNum === 11) {
            endHour = '12';
            endAmPm = startAmPm === 'AM' ? 'PM' : 'AM'; // Flip AM/PM
          } else {
            endHour = String(startHourNum + 1).padStart(2, '0');
            endAmPm = startAmPm;
          }
          endMinute = startMinute;
        }
      }
      
      // Parse participants if exist
      const participantList = [];
      console.log('🔍 Loading event participants:', eventToEdit.participants);
      console.log('🔍 Available family participants:', commonParticipants.map(p => ({ id: p.id, name: p.name, email: p.email })));
      
      // Map old names to new generic roles
      const nameToRoleMapping = {
        'Roger': 'dad',
        'roger': 'dad',
        'Iryna': 'mom',
        'iryna': 'mom',
        'Zeus': 'kid',
        'zeus': 'kid',
        'Rashid-Driver': 'nanny',
        'rashid-driver': 'nanny',
        'Mimi-Nanny': 'nanny',
        'mimi-nanny': 'nanny',
        'Rashid': 'nanny',
        'rashid': 'nanny',
        'Mimi': 'nanny',
        'mimi': 'nanny',
        'Dad': 'dad',
        'dad': 'dad',
        'Mom': 'mom',
        'mom': 'mom',
        'Kid': 'kid',
        'kid': 'kid',
        'Nanny': 'nanny',
        'nanny': 'nanny'
      };
      
      // First check if we have the new participants array format
      if (eventToEdit.participants && Array.isArray(eventToEdit.participants)) {
        eventToEdit.participants.forEach(emailOrName => {
          // First try to match by email (new format)
          const matchByEmail = (commonParticipants || []).find(p => p.email === emailOrName);
          if (matchByEmail && !participantList.includes(matchByEmail.id)) {
            participantList.push(matchByEmail.id);
            console.log(`✓ Matched by email: ${emailOrName} -> ${matchByEmail.name} (${matchByEmail.id})`);
            return;
          }
          
          // Then try to match by ID (in case the participant array contains IDs)
          const matchById = (commonParticipants || []).find(p => p.id === emailOrName);
          if (matchById && !participantList.includes(matchById.id)) {
            participantList.push(matchById.id);
            console.log(`✓ Matched by ID: ${emailOrName} -> ${matchById.name}`);
            return;
          }
          
          // Then check if it's an old name that needs mapping
          const roleId = nameToRoleMapping[emailOrName] || nameToRoleMapping[emailOrName.toLowerCase()];
          if (roleId && !participantList.includes(roleId)) {
            participantList.push(roleId);
            console.log(`✓ Matched by role mapping: ${emailOrName} -> ${roleId}`);
          } else {
            // Also check if it matches any of the new participant names
            const match = (commonParticipants || []).find(p => p.name === emailOrName || p.name.toLowerCase() === emailOrName.toLowerCase());
            if (match && !participantList.includes(match.id)) {
              participantList.push(match.id);
              console.log(`✓ Matched by name: ${emailOrName} -> ${match.name} (${match.id})`);
            } else {
              console.log(`✗ No match found for: ${emailOrName}`);
            }
          }
        });
      } 
      // Fall back to the old participant string format if needed
      else if (eventToEdit.participant) {
        const participants = eventToEdit.participant.split(',').map(p => p.trim());
        participants.forEach(name => {
          // Check if it's an old name that needs mapping
          const roleId = nameToRoleMapping[name] || nameToRoleMapping[name.toLowerCase()];
          if (roleId && !participantList.includes(roleId)) {
            participantList.push(roleId);
          } else {
            // Also check if it matches any of the new participant names
            const match = (commonParticipants || []).find(p => p.name === name || p.id === name.toLowerCase());
            if (match && !participantList.includes(match.id)) {
              participantList.push(match.id);
            }
          }
        });
      }
      
      console.log('✅ Final matched participant IDs:', participantList);
      
      // Update form data with the event's values
      setFormData({
        eventName: eventToEdit.eventName || '',
        description: eventToEdit.description || '',
        date: dateStr,
        hasTime: eventToEdit.hasTime || false,
        isAllDay: eventToEdit.isAllDay || true,
        startHour,
        startMinute,
        startAmPm,
        endHour,
        endMinute,
        endAmPm,
        eventDuration: eventToEdit.eventDuration || 60, // Default 1 hour if not specified
        timeInputValue: `${startHour}${startMinute}`, // Set initial time input value
        endTimeInputValue: `${endHour}${endMinute}`, // Set initial end time input value
        participants: participantList,
        location: eventToEdit.location || '',
        category: eventToEdit.category || 'life',
        isRecurring: eventToEdit.isRecurring || false,
        recurrencePattern: eventToEdit.recurrencePattern || 'custom',
        recurrenceEndDate: eventToEdit.recurrenceEndDate || '',
        recurrenceCount: eventToEdit.recurrenceCount || 0,
        recurrenceInterval: eventToEdit.recurrenceInterval || 1,
        selectedDays: eventToEdit.selectedDays || {
          monday: false,
          tuesday: false,
          wednesday: false,
          thursday: false,
          friday: false,
          saturday: false,
          sunday: false
        }
      });
      
      setShowDetailedForm(true);
    } else {
      // For new events, initialize with time always shown
      setFormData(prev => ({
        ...prev,
        hasTime: true,
        isAllDay: false
      }));
    }
  }, [eventToEdit, commonParticipants]);

  const handleParticipantToggle = (participantId) => {
    setFormData(prev => {
      const participants = [...prev.participants];
      
      if (participants.includes(participantId)) {
        return { ...prev, participants: participants.filter(id => id !== participantId) };
      } else {
        return { ...prev, participants: [...participants, participantId] };
      }
    });
  };

  const handleLocationSelect = (locationId) => {
    const location = commonLocations.find(loc => loc.id === locationId);
    if (location) {
      setFormData(prev => ({ ...prev, location: location.name }));
    }
  };
  
  // Function to toggle day selection for custom recurrence
  const toggleDaySelection = (day) => {
    setFormData(prev => ({
      ...prev,
      selectedDays: {
        ...prev.selectedDays,
        [day]: !prev.selectedDays[day]
      }
    }));
  };

  const handleSubmit = (e) => {
    if (e) {
      e.preventDefault();
    }
    
    // Check if date is empty and set a default if needed
    if (!formData.date) {
      // Set to current date if empty
      setFormData(prev => ({ 
        ...prev, 
        date: getCurrentDate().toISOString().split('T')[0] 
      }));
      return; // Stop form submission to allow the state update to take effect
    }
    
    console.log('🖊️ ManualEventForm handleSubmit called');
    
    // Process the start time
    let startHour = parseInt(formData.startHour, 10);
    let startMinute = parseInt(formData.startMinute, 10);
    
    if (formData.startAmPm === 'PM' && startHour < 12) {
      startHour += 12;
    } else if (formData.startAmPm === 'AM' && startHour === 12) {
      startHour = 0;
    }
    
    // Process the end time
    let endHour = parseInt(formData.endHour, 10);
    let endMinute = parseInt(formData.endMinute, 10);
    
    if (formData.endAmPm === 'PM' && endHour < 12) {
      endHour += 12;
    } else if (formData.endAmPm === 'AM' && endHour === 12) {
      endHour = 0;
    }
    
    // Calculate event duration in minutes
    let eventDuration = 60; // Default to 1 hour if calculation fails
    
    if (formData.hasTime) {
      const startTimeMinutes = startHour * 60 + startMinute;
      const endTimeMinutes = endHour * 60 + endMinute;
      
      // Handle case when end time is on the next day
      if (endTimeMinutes < startTimeMinutes) {
        eventDuration = (24 * 60 - startTimeMinutes) + endTimeMinutes;
      } else {
        eventDuration = endTimeMinutes - startTimeMinutes;
      }
      
      // If duration is 0 or negative, default to 1 hour
      if (eventDuration <= 0) {
        eventDuration = 60;
      }
    }
    
    // Parse the date
    const dateParts = formData.date.split('-');
    const eventDate = new Date(
      parseInt(dateParts[0], 10),
      parseInt(dateParts[1], 10) - 1,
      parseInt(dateParts[2], 10)
    );
    
    console.log('📅 Parsed event date:', eventDate);
    
    // Map participant IDs to emails for filtering (keep emails for event visibility logic)
    const participantEmails = formData.participants.length > 0 
      ? formData.participants.map(id => {
          const participant = commonParticipants.find(p => p.id === id);
          return participant ? participant.email : id;
        }).filter(email => email) // Remove any undefined values
      : [];
    
    // Also create participant names for display purposes  
    const participantNames = formData.participants.length > 0 
      ? formData.participants.map(id => {
          const participant = commonParticipants.find(p => p.id === id);
          return participant ? participant.name : id;
        })
      : [];
    
    // Create event object
    const eventData = {
      day: eventDate.getDate(),
      month: eventDate.getMonth() + 1,
      year: eventDate.getFullYear(),
      hasTime: formData.hasTime,
      hour: formData.hasTime ? startHour : null,
      minute: formData.hasTime ? startMinute : null,
      endHour: formData.hasTime ? endHour : null,
      endMinute: formData.hasTime ? endMinute : null,
      eventDuration: eventDuration, // Duration in minutes
      isAllDay: !formData.hasTime,
      timeText: formData.hasTime ? `${formData.startHour}:${formData.startMinute} ${formData.startAmPm}` : 'All day',
      eventName: formData.eventName,
      description: formData.description,
      location: formData.location || null,
      participant: participantEmails.join(', '), // Use emails for filtering logic
      participants: participantEmails, // Use emails for filtering logic (family role-based event visibility)
      participantNames: participantNames, // Store names for display purposes
      category: formData.category,
      originalDateText: formData.date,
      
      // If editing an existing event, keep its ID; otherwise manually generate an ID
      // This ID will be replaced by Firestore's document ID later
      id: eventToEdit ? eventToEdit.id : 'temp-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9),
      
      // Preserve createdAt timestamp if editing, otherwise create new one
      createdAt: eventToEdit?.createdAt || new Date().toISOString(),
      
      // Sharing properties
      sharedId: eventToEdit?.sharedId || null,
      sharedBy: eventToEdit?.sharedBy || null,
      sharedWith: eventToEdit?.sharedWith || null,
      sharedAt: eventToEdit?.sharedAt || null,
      
      // Recurrence properties
      isRecurring: formData.isRecurring,
      recurrencePattern: formData.isRecurring ? formData.recurrencePattern : null,
      recurrenceEndDate: formData.isRecurring && formData.recurrenceEndDate ? formData.recurrenceEndDate : null,
      recurrenceCount: formData.isRecurring ? formData.recurrenceCount : 0,
      recurrenceInterval: formData.isRecurring ? formData.recurrenceInterval : 1,
      selectedDays: formData.isRecurring ? formData.selectedDays : null,
      
      // Class event properties
      isClassEvent: formData.category === 'class',
      classID: formData.category === 'class' ? classInfo?.classID : null,
      className: formData.category === 'class' ? classInfo?.className : null
    };
    
    console.log('📝 Final event data to be sent:', eventData);
    
    // Call the callback to add this event
    console.log('📤 Calling onAddEvent prop function');
    
    // Track event creation
    trackCalendar.createEvent(
      'manual',
      formData.category,
      formData.hasTime
    );
    
    onAddEvent(eventData);
    
    // Show confirmation message - use shorter message on mobile
    if (isMobile) {
      setConfirmationMessage('Event added!');
    } else {
      setConfirmationMessage(`Event "${formData.eventName}" has been added!`);
    }
    setShowConfirmation(true);
    
    // Reference to the confirmation message element for animation
    const confirmationEl = document.getElementById('confirmation-message');
    
    // Hide confirmation after 3 seconds
    setTimeout(() => {
      if (confirmationEl) {
        // Add fadeOut animation
        confirmationEl.style.animation = 'fadeOut 0.5s ease forwards';
        
        // Wait for animation to complete before hiding
        setTimeout(() => {
          setShowConfirmation(false);
        }, 500);
      } else {
        setShowConfirmation(false);
      }
    }, 2500);
    
    // Reset the form if not editing
    if (!eventToEdit) {
      // Only reset form data for new events, not for edits
      const todayDate = new Date();
      setFormData({
        eventName: '',
        description: '',
        date: todayDate.toISOString().split('T')[0], // Ensure we use current date
        hasTime: true,
        isAllDay: false,
        startHour: '09',
        startMinute: '00',
        startAmPm: 'AM',
        endHour: '10',
        endMinute: '00',
        endAmPm: 'AM',
        timeInputValue: '0900', // Reset start time input
        endTimeInputValue: '1000', // Reset end time input
        eventDuration: 60, // Default 1 hour
        participants: [],
        location: '',
        category: 'life',
        isRecurring: false,
        recurrencePattern: 'custom',
        recurrenceEndDate: '',
        recurrenceCount: 0,
        recurrenceInterval: 1,
        selectedDays: {
          monday: false,
          tuesday: false,
          wednesday: false,
          thursday: false,
          friday: false,
          saturday: false,
          sunday: false
        }
      });
    }
  };

  
  // Add inline styles to override any potential internal scrolling
  const staticStyles = {
    overflow: 'visible !important',
    maxHeight: 'none !important',
    height: 'auto',
    position: 'static',
    display: 'block'
  };

  // Apply additional styles for desktop view
  const desktopStyles = !isMobile ? {
    overflow: 'visible !important',
    height: 'auto !important',
    maxHeight: 'none !important',
    marginBottom: '50px',
    overflowY: 'visible',
  } : {};

  const formContentStyles = !isMobile ? {
    ...staticStyles,
    overflow: 'visible !important',
    overflowY: 'visible !important',
    maxHeight: 'none !important',
    paddingBottom: '30px'
  } : staticStyles;

  return (
    <div className="neo-form" style={{...staticStyles, ...desktopStyles}}>
        <div className="neo-form-header">
          <h3 style={{ fontSize: '16px' }}>Add Event</h3>
          {onClose && (
            <button 
              type="button"
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '0',
                marginLeft: 'auto',
                lineHeight: '1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '30px',
                height: '30px'
              }}
              aria-label="Close"
            >
              ×
            </button>
          )}
        </div>
        
        {/* Confirmation Message */}
        {showConfirmation && (
          <div id="confirmation-message" className={`neo-confirmation ${isMobile ? 'mobile' : ''}`}>
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
            <span>{confirmationMessage}</span>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="neo-form-content" style={formContentStyles}>
        <div className="neo-field">
          <label className="neo-label-sm" htmlFor="eventName">Event Name</label>
          <input
            type="text"
            id="eventName"
            name="eventName"
            className="neo-input"
            value={formData.eventName}
            onChange={handleInputChange}
            placeholder="Event Name"
            required
          />
        </div>
        
        {/* Date Field */}
        <div className="neo-row">
          <div className="neo-field" style={{flex: '1'}}>
            <label className="neo-label-sm">Date</label>
            <div className="date-field-container">
              {isMobile ? (
                // Mobile-friendly date input without the calendar icon
                <input
                  type="date"
                  id="date"
                  name="date"
                  className="neo-input mobile-date-fix"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                  style={{fontSize: '14px', padding: '8px 5px'}} 
                />
              ) : (
                // Standard date input for desktop without the calendar icon
                <input
                  type="date"
                  id="date"
                  name="date"
                  className="neo-input"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                  style={!isMobile ? {width: '100%', padding: '10px'} : {}}
                />
              )}
            </div>
          </div>
        </div>

        {/* Time Fields */}
        <div className="neo-row" style={{ marginTop: '10px' }}>
          <div className="neo-field" style={{flex: '1'}}>
            <label className="neo-label-sm">Time</label>
            <div style={{display: 'flex', justifyContent: 'space-between', gap: '6px'}}>
              {/* Start Time - Button to open time picker */}
              <div className="neo-time-button" style={{flex: '1'}}>
                <button
                  type="button"
                  className="neo-time-picker-button"
                  onClick={() => setShowStartTimeModal(true)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    background: 'white',
                    border: '1px solid var(--neo-border)',
                    borderRadius: '10px',
                    fontSize: '14px',
                    color: 'var(--neo-text)',
                    boxShadow: 'var(--neo-shadow-sm)',
                    textAlign: 'center',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '5px'
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                  {formData.startHour}:{formData.startMinute} {formData.startAmPm}
                </button>
                
                {/* Time picker modal */}
                <TimeInputModal
                  isOpen={showStartTimeModal}
                  onClose={() => setShowStartTimeModal(false)}
                  initialHour={formData.startHour}
                  initialMinute={formData.startMinute}
                  initialAmPm={formData.startAmPm}
                  label="Start Time"
                  onTimeSelected={({ hour, minute, amPm }) => {
                    setFormData(prev => ({
                      ...prev,
                      startHour: hour,
                      startMinute: minute,
                      startAmPm: amPm,
                      timeInputValue: hour + minute
                    }));
                  }}
                />
              </div>
              
              {/* Separator */}
              <div style={{display: 'flex', alignItems: 'center', padding: '0 2px'}}>
                <span style={{fontSize: '14px', color: '#666'}}>-</span>
              </div>
              
              {/* End Time - Button to open time picker */}
              <div className="neo-time-button" style={{flex: '1'}}>
                <button
                  type="button"
                  className="neo-time-picker-button"
                  onClick={() => setShowEndTimeModal(true)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    background: 'white',
                    border: '1px solid var(--neo-border)',
                    borderRadius: '10px',
                    fontSize: '14px',
                    color: 'var(--neo-text)',
                    boxShadow: 'var(--neo-shadow-sm)',
                    textAlign: 'center',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '5px'
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                  {formData.endHour}:{formData.endMinute} {formData.endAmPm}
                </button>
                
                {/* Time picker modal */}
                <TimeInputModal
                  isOpen={showEndTimeModal}
                  onClose={() => setShowEndTimeModal(false)}
                  initialHour={formData.endHour}
                  initialMinute={formData.endMinute}
                  initialAmPm={formData.endAmPm}
                  label="End Time"
                  onTimeSelected={({ hour, minute, amPm }) => {
                    setFormData(prev => ({
                      ...prev,
                      endHour: hour,
                      endMinute: minute,
                      endAmPm: amPm,
                      endTimeInputValue: hour + minute
                    }));
                  }}
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Category Selector */}
        <div className="neo-field">
          <label className="neo-label-sm">Category</label>
          <div className="neo-category-selector">
            {categories.map(category => (
              <button
                key={category.id}
                type="button"
                className={`neo-category ${category.id} ${formData.category === category.id ? 'active' : ''}`}
                onClick={() => setFormData(prev => ({ ...prev, category: category.id }))}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
        
        {/* Location */}
        <div className="neo-field">
          <label className="neo-label-sm">Location</label>
          <div>
            <input
              type="text"
              id="location"
              name="location"
              className="neo-input"
              value={formData.location}
              onChange={handleInputChange}
              placeholder="Enter or choose one option below"
            />
          </div>
          
          <div className="neo-chip-container">
            {commonLocations.map(location => (
              <button
                key={location.id}
                type="button"
                className={`neo-chip ${formData.location === location.name ? 'active' : ''}`}
                onClick={() => handleLocationSelect(location.id)}
              >
                {location.name}
              </button>
            ))}
            <button
              type="button"
              className="neo-chip"
              onClick={() => setShowAddLocation(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
            </button>
          </div>
        </div>
        
        {/* Participants */}
        <div className="neo-field">
          <label className="neo-label-sm" style={{ marginBottom: '4px' }}>Who's Involved</label>
          <div className="neo-chip-container">
            {getAllParticipants().map(person => (
              <button
                key={person.id}
                type="button"
                className={`neo-chip person ${formData.participants.includes(person.id) ? 'active' : ''}`}
                onClick={() => handleParticipantToggle(person.id)}
              >
                {person.name}
                {person.custom && (
                  <span style={{marginLeft: '4px', fontSize: '10px', opacity: 0.7}}>*</span>
                )}
              </button>
            ))}
            <button
              type="button"
              onClick={openParticipantEditor}
              className="neo-chip"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
            </button>
          </div>
        </div>
        
        {/* Description */}
        <div className="neo-field">
          <textarea
            id="description"
            name="description"
            className="neo-textarea"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Description"
            rows="2"
          />
        </div>
        
        {/* Recurring Event */}
        <div className="neo-field">
          <div 
            className="neo-switch-container" 
            onClick={() => setFormData(prev => ({ ...prev, isRecurring: !prev.isRecurring }))}
            style={{ 
              position: 'relative',
              cursor: 'pointer',
              userSelect: 'none',
              ...(isMobile ? {
                padding: '12px',
                borderRadius: '12px',
                transition: 'all 0.2s ease'
              } : {})
            }}
          >
            <label className="neo-switch" onClick={e => e.stopPropagation()}>
              <input
                type="checkbox"
                checked={formData.isRecurring}
                onChange={e => setFormData(prev => ({ ...prev, isRecurring: e.target.checked }))}
              />
              <span className="neo-slider"></span>
            </label>
            <span className="neo-switch-label">Recurring Event</span>
          </div>
          
          {formData.isRecurring && (
            <div className="neo-accordion-content">
              <div className="neo-field">
                <label className="neo-label-sm">Repeat on</label>
                <div className="neo-weekday-selector" style={isMobile ? {gap: '2px'} : {}}>
                  <button
                    type="button"
                    className={`neo-weekday ${formData.selectedDays.sunday ? 'active' : ''}`}
                    onClick={() => toggleDaySelection('sunday')}
                    style={isMobile ? {fontSize: '10px', padding: '7px 0', minHeight: '36px'} : {}}
                  >
                    {isMobile ? 'S' : 'Sun'}
                  </button>
                  <button
                    type="button"
                    className={`neo-weekday ${formData.selectedDays.monday ? 'active' : ''}`}
                    onClick={() => toggleDaySelection('monday')}
                    style={isMobile ? {fontSize: '10px', padding: '7px 0', minHeight: '36px'} : {}}
                  >
                    {isMobile ? 'M' : 'Mon'}
                  </button>
                  <button
                    type="button"
                    className={`neo-weekday ${formData.selectedDays.tuesday ? 'active' : ''}`}
                    onClick={() => toggleDaySelection('tuesday')}
                    style={isMobile ? {fontSize: '10px', padding: '7px 0', minHeight: '36px'} : {}}
                  >
                    {isMobile ? 'T' : 'Tue'}
                  </button>
                  <button
                    type="button"
                    className={`neo-weekday ${formData.selectedDays.wednesday ? 'active' : ''}`}
                    onClick={() => toggleDaySelection('wednesday')}
                    style={isMobile ? {fontSize: '10px', padding: '7px 0', minHeight: '36px'} : {}}
                  >
                    {isMobile ? 'W' : 'Wed'}
                  </button>
                  <button
                    type="button"
                    className={`neo-weekday ${formData.selectedDays.thursday ? 'active' : ''}`}
                    onClick={() => toggleDaySelection('thursday')}
                    style={isMobile ? {fontSize: '10px', padding: '7px 0', minHeight: '36px'} : {}}
                  >
                    {isMobile ? 'T' : 'Thu'}
                  </button>
                  <button
                    type="button"
                    className={`neo-weekday ${formData.selectedDays.friday ? 'active' : ''}`}
                    onClick={() => toggleDaySelection('friday')}
                    style={isMobile ? {fontSize: '10px', padding: '7px 0', minHeight: '36px'} : {}}
                  >
                    {isMobile ? 'F' : 'Fri'}
                  </button>
                  <button
                    type="button"
                    className={`neo-weekday ${formData.selectedDays.saturday ? 'active' : ''}`}
                    onClick={() => toggleDaySelection('saturday')}
                    style={isMobile ? {fontSize: '10px', padding: '7px 0', minHeight: '36px'} : {}}
                  >
                    {isMobile ? 'S' : 'Sat'}
                  </button>
                </div>
              </div>
              
              <div className="neo-field">
                <label className="neo-label-sm">Ends</label>
                <div className="neo-radio-group">
                  <label className="neo-radio" style={isMobile ? {display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'nowrap', marginBottom: '6px'} : {}}>
                    <input 
                      type="radio"
                      name="endType" 
                      checked={formData.recurrenceCount === 0 && !formData.recurrenceEndDate}
                      onChange={() => setFormData(prev => ({ 
                        ...prev, 
                        recurrenceCount: 0,
                        recurrenceEndDate: ''  
                      }))}
                      style={isMobile ? {marginRight: '3px'} : {}}
                    />
                    <span className="neo-radio-label" style={isMobile ? {fontSize: '13px'} : {}}>Never</span>
                  </label>
                  
                  <label className="neo-radio" style={isMobile ? {display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'nowrap', marginBottom: '6px'} : {}}>
                    <input 
                      type="radio"
                      name="endType"
                      checked={formData.recurrenceCount > 0}
                      onChange={() => setFormData(prev => ({ 
                        ...prev, 
                        recurrenceCount: prev.recurrenceCount || 10,
                        recurrenceEndDate: ''  
                      }))}
                      style={isMobile ? {marginRight: '3px'} : {}}
                    />
                    <span className="neo-radio-label" style={isMobile ? {fontSize: '13px'} : {}}>After</span>
                    <div style={{display: 'flex', alignItems: 'center'}}>
                      <input 
                        type="number" 
                        min="1" 
                        max="100"
                        disabled={formData.recurrenceCount === 0}
                        value={formData.recurrenceCount || ''}
                        onClick={() => setFormData(prev => ({ 
                          ...prev, 
                          recurrenceCount: prev.recurrenceCount || 10,
                          recurrenceEndDate: '' 
                        }))}
                        onChange={e => setFormData(prev => ({ 
                          ...prev, 
                          recurrenceCount: parseInt(e.target.value) || 0,
                          recurrenceEndDate: ''
                        }))}
                        className="neo-input number small"
                        style={isMobile ? {width: '45px', fontSize: '13px', marginLeft: '5px'} : {}}
                      />
                      <span 
                        className="neo-radio-text" 
                        style={isMobile ? {fontSize: '12px'} : {}}
                        onClick={() => setFormData(prev => ({ 
                          ...prev, 
                          recurrenceCount: prev.recurrenceCount || 10,
                          recurrenceEndDate: '' 
                        }))}
                      >occurrences</span>
                    </div>
                  </label>
                  
                  <label className="neo-radio" style={isMobile ? {display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'nowrap'} : {}}>
                    <input 
                      type="radio"
                      name="endType"
                      checked={!!formData.recurrenceEndDate}
                      style={isMobile ? {marginRight: '3px'} : {}}
                      onChange={() => {
                        // Set default end date to 1 month from now
                        const endDate = new Date();
                        endDate.setMonth(endDate.getMonth() + 1);
                        setFormData(prev => ({ 
                          ...prev, 
                          recurrenceCount: 0,
                          recurrenceEndDate: endDate.toISOString().split('T')[0]  
                        }));
                      }}
                    />
                    <span className="neo-radio-label" style={isMobile ? {fontSize: '13px'} : {}}>On</span>
                    <div style={{display: 'flex', alignItems: 'center', flex: 1}}>
                      <input 
                        type="date"
                        disabled={!formData.recurrenceEndDate}
                        value={formData.recurrenceEndDate}
                        onClick={() => {
                          if (!formData.recurrenceEndDate) {
                            // Set default end date to 1 month from now
                            const endDate = new Date();
                            endDate.setMonth(endDate.getMonth() + 1);
                            setFormData(prev => ({ 
                              ...prev, 
                              recurrenceCount: 0,
                              recurrenceEndDate: endDate.toISOString().split('T')[0]  
                            }));
                          }
                        }}
                        onChange={e => setFormData(prev => ({ 
                          ...prev, 
                          recurrenceEndDate: e.target.value,
                          recurrenceCount: 0
                        }))}
                        className="neo-input date mobile-date-fix"
                        style={isMobile ? {fontSize: '13px', marginLeft: '5px', width: '130px'} : {}}
                      />
                    </div>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="neo-action-buttons" style={!isMobile ? {marginBottom: '30px', marginTop: '20px'} : {}}>
          <button
            type="submit"
            className="neo-button primary"
            style={{ width: '100%' }}
          >
            Add Event
          </button>
        </div>
      </form>
      
      {/* PARTICIPANT EDITOR MODAL - Modal for adding/removing participants */}
      {showParticipantEditor && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          padding: '20px',
          paddingTop: '12vh'
        }}>
          <div className="modal-container" style={{
            backgroundColor: 'white',
            borderRadius: '20px',
            boxShadow: '0 5px 15px rgba(0, 0, 0, 0.3)',
            maxWidth: '400px',
            width: '90%',
            maxHeight: '70vh',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div className="neo-form-header" style={{
              padding: '15px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid #eee',
              backgroundColor: '#2A9D8F',
              color: 'white',
              borderRadius: '20px 20px 0 0'
            }}>
              <h3 style={{ fontSize: '16px', color: 'white', margin: 0 }}>Edit Participants</h3>
              <button 
                onClick={closeParticipantEditor}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: 'white'
                }}
              >×</button>
            </div>
            <div style={{padding: '20px', maxHeight: 'calc(70vh - 70px)', overflowY: 'auto'}}>
              <div style={{marginBottom: '15px'}}>
                <label className="neo-label-sm">Current Participants</label>
                <div style={{marginTop: '8px'}}>
                  {getAllParticipants().map(person => (
                    <div key={person.id} style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', padding: '8px', border: '1px solid #eee', borderRadius: '8px'}}>
                      <span style={{flex: 1}}>
                        {person.name}
                        {person.custom && <span style={{marginLeft: '4px', fontSize: '10px', opacity: 0.7, color: '#666'}}>*</span>}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveClick(person)}
                        style={{
                          background: '#ff4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '4px 8px',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              
              <div style={{marginBottom: '15px'}}>
                <label className="neo-label-sm">Add New Participant</label>
                <div style={{display: 'flex', gap: '8px', marginTop: '8px'}}>
                  <input
                    type="text"
                    placeholder="Enter name"
                    className="neo-input"
                    style={{flex: 1}}
                    onKeyPress={async (e) => {
                      if (e.key === 'Enter') {
                        const name = e.target.value.trim();
                        if (name) {
                          await addCustomParticipant(name);
                          e.target.value = '';
                        }
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={async (e) => {
                      const input = e.target.parentElement.querySelector('input');
                      const name = input.value.trim();
                      if (name) {
                        await addCustomParticipant(name);
                        input.value = '';
                      }
                    }}
                    style={{
                      background: '#2A9D8F',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '8px 12px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    Add
                  </button>
                </div>
              </div>
              
              <div style={{display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px'}}>
                <button
                  type="button"
                  onClick={closeParticipantEditor}
                  className="neo-button secondary"
                  style={{fontSize: '14px', padding: '8px 16px'}}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* CONFIRMATION DIALOG - Confirm family member removal */}
      {showRemoveConfirmation && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 10001,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          padding: '20px',
          paddingTop: '12vh'
        }}>
          <div className="modal-container" style={{
            backgroundColor: 'white',
            borderRadius: '20px',
            boxShadow: '0 5px 15px rgba(0, 0, 0, 0.3)',
            maxWidth: '350px',
            width: '90%',
            maxHeight: '300px',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div className="neo-form-header" style={{
              padding: '15px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid #eee',
              backgroundColor: '#2A9D8F',
              color: 'white',
              borderRadius: '20px 20px 0 0'
            }}>
              <h3 style={{ fontSize: '16px', color: 'white', margin: 0 }}>Confirm Removal</h3>
            </div>
            <div style={{padding: '20px', textAlign: 'center'}}>
              <p style={{marginBottom: '20px', fontSize: '14px', lineHeight: '1.4'}}>
                Are you sure you want to permanently remove <strong>{memberToRemove?.name}</strong> from your family? 
                This will remove them from all family events and cannot be undone.
              </p>
              <div style={{display: 'flex', justifyContent: 'center', gap: '10px'}}>
                <button
                  type="button"
                  onClick={confirmRemoveMember}
                  style={{
                    background: '#ff4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '10px 20px',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  Remove
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowRemoveConfirmation(false);
                    setMemberToRemove(null);
                  }}
                  className="neo-button secondary"
                  style={{fontSize: '14px', padding: '10px 20px'}}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ADD LOCATION MODAL */}
      {showAddLocation && (
        <div className="modal-overlay" style={{zIndex: 10001}}>
          <div className="modal-container" style={{maxWidth: '400px'}}>
            <div className="neo-form-header">
              <h3 style={{ fontSize: '16px', color: 'white' }}>Add New Location</h3>
              <button 
                onClick={() => {
                  setShowAddLocation(false);
                  setNewLocationName('');
                }}
                className="neo-close-button"
              >
                ×
              </button>
            </div>
            <div className="neo-form-content" style={{padding: '20px'}}>
              <div className="neo-field">
                <label className="neo-label-sm">Location Name</label>
                <input
                  type="text"
                  className="neo-input"
                  value={newLocationName}
                  onChange={(e) => setNewLocationName(e.target.value)}
                  placeholder="Enter location name"
                  autoFocus
                />
              </div>
              <div style={{display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px'}}>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddLocation(false);
                    setNewLocationName('');
                  }}
                  className="neo-button secondary"
                  style={{fontSize: '14px', padding: '8px 16px'}}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (newLocationName.trim()) {
                      const newLocation = {
                        id: newLocationName.toLowerCase().replace(/\s+/g, '-'),
                        name: newLocationName.trim()
                      };
                      setCommonLocations(prev => [...prev, newLocation]);
                      setFormData(prev => ({ ...prev, location: newLocation.name }));
                      setNewLocationName('');
                      setShowAddLocation(false);
                    }
                  }}
                  className="neo-button primary"
                  style={{fontSize: '14px', padding: '8px 16px'}}
                  disabled={!newLocationName.trim()}
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManualEventForm;